import { supabase } from '../lib/supabase';

/**
 * Logo Service
 * Handles logo upload, retrieval, and deletion operations
 */

const BUCKET_NAME = 'logos';
const LOGO_SETTING_KEY = 'app_logo_url';

/**
 * Upload logo to Supabase Storage
 * @param {File} file - Logo file to upload
 * @returns {Promise<{success: boolean, url?: string, error?: string}>}
 */
export const uploadLogo = async (file) => {
  try {
    if (!file) {
      return { success: false, error: 'No file provided' };
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes?.includes(file?.type)) {
      return { success: false, error: 'Invalid file type. Please upload JPG, PNG, WebP, or SVG.' };
    }

    // Validate file size (2MB)
    if (file?.size > 2097152) {
      return { success: false, error: 'File size exceeds 2MB limit.' };
    }

    // Delete old logo if exists
    const oldLogoUrl = await getLogoUrl();
    if (oldLogoUrl) {
      const oldFileName = oldLogoUrl?.split('/')?.pop();
      if (oldFileName) {
        await supabase?.storage?.from(BUCKET_NAME)?.remove([oldFileName]);
      }
    }

    // Generate unique filename
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `logo-${Date.now()}.${fileExt}`;

    // Upload to storage
    const { data: uploadData, error: uploadError } = await supabase?.storage
      ?.from(BUCKET_NAME)
      ?.upload(fileName, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (uploadError) {
      return { success: false, error: uploadError?.message };
    }

    // Get public URL
    const { data: urlData } = supabase?.storage
      ?.from(BUCKET_NAME)
      ?.getPublicUrl(fileName);

    const publicUrl = urlData?.publicUrl;

    if (!publicUrl) {
      return { success: false, error: 'Failed to get public URL' };
    }

    // Save URL to app_settings
    const { error: settingsError } = await supabase
      ?.from('app_settings')
      ?.upsert({
        setting_key: LOGO_SETTING_KEY,
        setting_value: publicUrl,
        updated_by: (await supabase?.auth?.getUser())?.data?.user?.id,
        updated_at: new Date()?.toISOString()
      }, {
        onConflict: 'setting_key'
      });

    if (settingsError) {
      return { success: false, error: settingsError?.message };
    }

    return { success: true, url: publicUrl };
  } catch (error) {
    return { success: false, error: error?.message || 'Failed to upload logo' };
  }
};

/**
 * Get current logo URL from app_settings
 * @returns {Promise<string|null>}
 */
export const getLogoUrl = async () => {
  try {
    const { data, error } = await supabase
      ?.from('app_settings')
      ?.select('setting_value')
      ?.eq('setting_key', LOGO_SETTING_KEY)
      ?.single();

    if (error) {
      if (error?.code === 'PGRST116') {
        return null;
      }
      throw error;
    }

    return data?.setting_value || null;
  } catch (error) {
    console.error('Error fetching logo URL:', error?.message);
    return null;
  }
};

/**
 * Delete current logo
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteLogo = async () => {
  try {
    const logoUrl = await getLogoUrl();
    if (!logoUrl) {
      return { success: false, error: 'No logo to delete' };
    }

    // Extract filename from URL
    const fileName = logoUrl?.split('/')?.pop();
    if (!fileName) {
      return { success: false, error: 'Invalid logo URL' };
    }

    // Delete from storage
    const { error: storageError } = await supabase?.storage
      ?.from(BUCKET_NAME)
      ?.remove([fileName]);

    if (storageError) {
      return { success: false, error: storageError?.message };
    }

    // Clear from app_settings
    const { error: settingsError } = await supabase
      ?.from('app_settings')
      ?.update({
        setting_value: '',
        updated_by: (await supabase?.auth?.getUser())?.data?.user?.id,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('setting_key', LOGO_SETTING_KEY);

    if (settingsError) {
      return { success: false, error: settingsError?.message };
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: error?.message || 'Failed to delete logo' };
  }
};